import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAvailablePeriodsForComparison } from '../services/api';
import { toast } from 'react-hot-toast';

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  closingTypeLabel?: string;
  fiscalYear: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
  };
}

interface PeriodComparisonState {
  availablePeriods: Period[];
  isLoading: boolean;
  error: string | null;
  lastFetch: number | null;

  fetchAvailablePeriods: (forceRefresh?: boolean) => Promise<void>;
  refreshData: () => Promise<void>;
  clearError: () => void;
}


const usePeriodComparisonStore = create<PeriodComparisonState>()(
  persist(
    (set, get) => ({
  availablePeriods: [],
  isLoading: false,
  error: null,
  lastFetch: null,
  
  fetchAvailablePeriods: async (forceRefresh = false) => {
    const { lastFetch, isLoading, availablePeriods } = get();
    const now = Date.now();

    // 如果正在載入，直接返回
    if (isLoading) {
      return;
    }

    // 檢查快取是否有效（除非強制刷新）
    if (!forceRefresh && lastFetch) {
      return;
    }

    // 設定載入狀態（只有在從未載入過資料時才顯示 loading）
    const shouldShowLoading = !lastFetch || forceRefresh;
    set({
      error: null,
      isLoading: shouldShowLoading
    });

    try {
      const response = await getAvailablePeriodsForComparison();
      set({
        availablePeriods: response.data || [],
        lastFetch: now,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || '無法載入可比較期間';
      console.error('載入可比較期間失敗:', error);
      set({
        isLoading: false,
        error: errorMessage,
      });
      toast.error(errorMessage);
    }
  },

  refreshData: async () => {
    await get().fetchAvailablePeriods(true);
  },
  
  clearError: () => {
    set({ error: null });
  },
    }),
    {
      name: 'period-comparison-storage',
      partialize: (state) => ({
        availablePeriods: Array.isArray(state.availablePeriods) ? state.availablePeriods : [],
        lastFetch: state.lastFetch,
      }),
      // 從存儲恢復時確保數據類型正確
      merge: (persistedState: any, currentState: PeriodComparisonState) => ({
        ...currentState,
        ...persistedState,
        availablePeriods: Array.isArray(persistedState?.availablePeriods) ? persistedState.availablePeriods : [],
        isLoading: false, // 重新整理時不顯示載入狀態
        error: null, // 重置錯誤狀態
      }),
    }
  )
);

export default usePeriodComparisonStore;